import React, { Component } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { obfuscatedCoordinates, hasValidMapAccessToken } from '../../util/maps';
import { Heading, Map } from '../../components';

import { formatCoarseLocation } from '../../util/listingLocation';

import css from './ListingPage.module.css';

/**
 * The SectionMapMaybe component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {propTypes.latlng} [props.geolocation] - The geolocation
 * @param {propTypes.uuid} props.listingId - The listing id
 * @returns {JSX.Element} section map maybe component
 */
class SectionMapMaybe extends Component {
  constructor(props) {
    super(props);
    this.state = { isStatic: true };
  }

  render() {
    const { className, rootClassName, geolocation, publicData, listingId, mapsConfig } = this.props;

    if (!geolocation) {
      return null;
    }

    // The published locality ("Springfield, IL"), never a street address — publicData no longer
    // carries one, and the stored geolocation is already fuzzed at write time (see
    // util/listingLocation.js), so this map shows an area rather than a doorstep by construction.
    const address = formatCoarseLocation(publicData?.location) || '';
    const classes = classNames(rootClassName || css.sectionMap, className);
    const cacheKey = listingId ? `${listingId.uuid}_${geolocation.lat}_${geolocation.lng}` : null;

    const mapProps = mapsConfig.fuzzy.enabled
      ? { obfuscatedCenter: obfuscatedCoordinates(geolocation, mapsConfig.fuzzy.offset, cacheKey) }
      : { address, center: geolocation };
    // Pass mapsConfig explicitly so the Map's availability check uses the same
    // config this component used to decide whether to render the toggle button.
    const map = <Map {...mapProps} mapsConfig={mapsConfig} useStaticMap={this.state.isStatic} />;

    // With no usable map key the Map renders a static fallback, so don't wrap it in
    // the static→dynamic toggle button (which would be a no-op clickable affordance).
    const hasInteractiveMap = hasValidMapAccessToken(mapsConfig);

    // Logistics/moving jobs go pickup → drop-off. Publicly we show only the straight-line distance
    // (not the exact drop-off address), matching the fuzzed pickup's privacy posture. Gate on
    // serviceType too (not just dropoffLocation presence) so a listing that was edited away from
    // 'logistics' never shows a stale route line.
    const hasDropoff = publicData?.serviceType === 'logistics' && !!publicData?.dropoffLocation;
    const distanceMiles =
      hasDropoff && Number.isFinite(publicData?.distanceMiles) ? publicData.distanceMiles : null;

    return (
      <section className={classes} id="listing-location">
        <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
          <FormattedMessage id="ListingPage.locationTitle" />
        </Heading>
        {hasInteractiveMap && this.state.isStatic ? (
          <button
            className={css.map}
            onClick={() => {
              this.setState({ isStatic: false });
            }}
          >
            {map}
          </button>
        ) : (
          <div className={css.map}>{map}</div>
        )}
        {distanceMiles !== null ? (
          <p className={css.routeDistance}>
            <FormattedMessage id="ListingPage.routeDistance" values={{ miles: distanceMiles }} />
          </p>
        ) : null}
      </section>
    );
  }
}

export default SectionMapMaybe;
